import React, { useEffect, useState } from 'react';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonModal,
  IonPage,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import {
  barbellOutline,
  calendarOutline,
  cubeOutline,
  downloadOutline,
  heartOutline,
  logoInstagram,
  lockClosedOutline,
  paperPlaneOutline,
  pawOutline,
  personCircleOutline,
  sparklesOutline,
} from 'ionicons/icons';
import { useHistory } from 'react-router-dom';
import './Home.css';
import LoginModal from '../login/LoginModal';
import RegisterModal from '../registration/RegisterModal';
import { publicSiteContent } from '../../config/PublicSiteContent';

const socialIcons: Record<string, string> = {
  Instagram: logoInstagram,
  Telegram: paperPlaneOutline,
};

const Home: React.FC = () => {
  const history = useHistory();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authTab, setAuthTab] = useState<'login' | 'register'>('login');
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);

  useEffect(() => {
    const handler = (event: any) => {
      event.preventDefault();
      setDeferredPrompt(event);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const openAuth = (tab: 'login' | 'register') => {
    setAuthTab(tab);
    setShowAuthModal(true);
  };
  const install = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
  };

  return (
    <IonPage className="landing-page">
      <IonHeader className="landing-header">
        <IonToolbar>
          <IonTitle>
            <span className="landing-brand">
              <img src="/assets/pl_icon/icon_f_matt.png" alt="" />
              PawerLifting
            </span>
          </IonTitle>
          <IonButtons slot="end">
            <IonButton
              shape="round"
              fill="clear"
              onClick={() => openAuth('login')}
            >
              <IonIcon icon={personCircleOutline} slot="start" />
              Log in
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen className="landing-content">
        <main>
          <section className="landing-hero">
            <div className="landing-hero__copy">
              <span className="landing-kicker">
                <IonIcon icon={heartOutline} />
                Fitness with a softer edge
              </span>
              <h1>Strength training that feels like it belongs to you.</h1>
              <p>
                Plan workouts, track real progress and learn your gym machines
                in a calm, privacy-minded fitness app built with heart.
              </p>
              <div className="landing-hero__actions">
                <IonButton shape="round" onClick={() => openAuth('register')}>
                  Join the beta
                </IonButton>
                <IonButton
                  shape="round"
                  fill="outline"
                  onClick={() =>
                    document
                      .getElementById('story')
                      ?.scrollIntoView({ behavior: 'smooth' })
                  }
                >
                  Meet the creator
                </IonButton>
              </div>
              <div className="landing-trust">
                <span>
                  <IonIcon icon={lockClosedOutline} />
                  Fitness data stays local
                </span>
                <span>
                  <IonIcon icon={sparklesOutline} />
                  Made independently in Germany
                </span>
              </div>
            </div>
            <div className="landing-hero__visual">
              <span className="landing-hero__energy landing-hero__energy--one" />
              <span className="landing-hero__energy landing-hero__energy--two" />
              <span className="landing-hero__energy landing-hero__energy--three" />
              <span className="landing-hero__halo" />
              <img
                src="/assets/ploppy/ploppy-training.png"
                alt="Ploppy, the PawerLifting mascot"
              />
              <aside className="landing-ploppy-badge">
                <span>Meet Ploppy</span>
                <strong>Your guide to your healthy body!</strong>
              </aside>
            </div>
          </section>

          <section
            className="landing-features"
            aria-label="PawerLifting features"
          >
            <article>
              <IonIcon icon={barbellOutline} />
              <span>Train simply</span>
              <h2>Fast workout logging</h2>
              <p>
                Add machines, choose the exact exercise and record every set
                without breaking your flow.
              </p>
            </article>
            <article>
              <IonIcon icon={lockClosedOutline} />
              <span>Local first</span>
              <h2>Private progress</h2>
              <p>
                Workout history, routines and personal fitness information are
                stored locally instead of becoming an advertising profile.
              </p>
            </article>
            <article>
              <IonIcon icon={downloadOutline} />
              <span>Install anywhere</span>
              <h2>A store-independent PWA</h2>
              <p>
                Install PawerLifting directly from the web, use it like an app
                and keep essential training features available offline.
              </p>
            </article>
          </section>

          <section
            className="landing-premium"
            aria-labelledby="landing-premium-title"
          >
            <div className="landing-premium__intro">
              <span className="landing-section-label">Premium features</span>
              <h2 id="landing-premium-title">
                More guidance when you want it.
              </h2>
            </div>
            <div className="landing-premium__cards">
              <article>
                <IonIcon icon={cubeOutline} />
                <div>
                  <span>Explore your body</span>
                  <h3>Interactive 3D advisor</h3>
                  <p>
                    Select a muscle and discover the machines and exercises that
                    target it.
                  </p>
                </div>
              </article>
              <article>
                <IonIcon icon={calendarOutline} />
                <div>
                  <span>Plan your rhythm</span>
                  <h3>Training calendar</h3>
                  <p>
                    Organise workouts, notes and recovery in one calm, personal
                    overview.
                  </p>
                </div>
              </article>
            </div>
          </section>

          <section id="story" className="landing-story">
            <div className="landing-story__portrait" tabIndex={0}>
              <img
                src="/assets/creator/creator.jpg"
                alt="Creator of PawerLifting"
              />
              <span />
            </div>
            <div>
              <span className="landing-section-label">Behind the app</span>
              <h2>Built by one person, for people who want less noise.</h2>
              <p>{publicSiteContent.about}</p>
              <p>
                PawerLifting is still a small beta. Feedback shapes what gets
                built next — from smarter exercise guidance to QR support with
                local gyms.
              </p>
              <div
                className="landing-socials"
                aria-label="Creator social media"
              >
                {publicSiteContent.socials.map(item =>
                  item.url ? (
                    <a
                      key={item.label}
                      href={item.url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <IonIcon icon={socialIcons[item.label]} />
                      {item.label}
                    </a>
                  ) : (
                    <button
                      key={item.label}
                      type="button"
                      aria-disabled="true"
                      title={`${item.label} link coming soon`}
                    >
                      <IonIcon icon={socialIcons[item.label]} />
                      {item.label}
                      <small>soon</small>
                    </button>
                  )
                )}
              </div>
            </div>
          </section>

          <section className="landing-shelter">
            <IonIcon icon={pawOutline} />
            <div>
              <span className="landing-section-label">
                Progress with purpose
              </span>
              <h2>Fitness can support more than fitness.</h2>
              <p>
                The long-term plan is to direct a percentage of subscription
                revenue from premium features to an animal shelter in Germany.
                The partner and transparent reporting will be announced before
                paid subscriptions begin.
              </p>
            </div>
          </section>

          <section className="landing-cta">
            <img src="/assets/ploppy/ploppy-finished.png" alt="Happy Ploppy" />
            <div>
              <span>Ready for your next set?</span>
              <h2>Help shape PawerLifting while it is still small.</h2>
              <div>
                <IonButton shape="round" onClick={() => openAuth('register')}>
                  Create account
                </IonButton>
                {deferredPrompt && (
                  <IonButton shape="round" fill="outline" onClick={install}>
                    <IonIcon icon={downloadOutline} slot="start" />
                    Install app
                  </IonButton>
                )}
              </div>
            </div>
          </section>
        </main>
      </IonContent>

      <IonFooter className="landing-footer">
        <div>
          <span>© {new Date().getFullYear()} PawerLifting</span>
          <button onClick={() => history.push('/privacy')}>Privacy</button>
          <button onClick={() => history.push('/imprint')}>Imprint</button>
          <button
            onClick={() =>
              window.dispatchEvent(new Event('pawerlifting-open-consent'))
            }
          >
            Privacy settings
          </button>
        </div>
      </IonFooter>

      <IonModal
        isOpen={showAuthModal}
        onDidDismiss={() => setShowAuthModal(false)}
        initialBreakpoint={0.9}
        breakpoints={[0, 0.9, 1]}
        className="landing-auth-modal"
      >
        <IonHeader>
          <IonToolbar>
            <IonTitle>
              {authTab === 'login' ? 'Welcome back' : 'Join PawerLifting'}
            </IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={() => setShowAuthModal(false)}>
                Close
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent className="landing-auth-content">
          <div className="landing-auth-tabs">
            <button
              className={authTab === 'login' ? 'is-active' : ''}
              onClick={() => setAuthTab('login')}
            >
              Log in
            </button>
            <button
              className={authTab === 'register' ? 'is-active' : ''}
              onClick={() => setAuthTab('register')}
            >
              Register
            </button>
          </div>
          {authTab === 'login' ? <LoginModal /> : <RegisterModal />}
        </IonContent>
      </IonModal>
    </IonPage>
  );
};

export default Home;
